/* @flow @format */

import { combineEpics, ofType } from 'redux-observable';
import { from, throwError } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { normalize } from 'normalizr';
import F from 'lodash/fp';
import JSZip from 'jszip';
import * as L from 'partial.lenses';

import { Qdl as ProcQDL } from '@sonyged/ext-proc';

import type { ActionType } from '../utils';
import {
  type CourseContent,
  type FetchEntitiesPayload,
  fetchEntitiesBegin,
  fetchEntitiesFailure,
  fetchEntitiesSuccess,
} from '../reducer/entities';
import { fetchProgressBegin } from '../reducer/progresses';
import type { ObservableAction } from './types';
import { catchWith, getData } from './utils';
import { contestSchema } from '../schema';
import { fetchRankingsBegin, fetchRankingsFailure, fetchRankingsSuccess } from '../reducer/ranking';
import api from '../../graphql/api';

type UidQdlTuple = [string, string];

const unpackZipQdl = (data: ArrayBuffer): Promise<string> => {
  const parser = new ProcQDL.Parser();
  const serializer = new ProcQDL.Serializer();

  return JSZip.loadAsync(data).then(zip => {
    const bookOriginalFile = zip.files['book.original.json'];
    if (bookOriginalFile) {
      console.info('Use original qdl');
      return bookOriginalFile.async('text');
    } else {
      console.info('Use packed qdl');
      return parser.parseZipPackAsync(data).then(model => JSON.stringify(serializer.serialize(model)));
    }
  });
};

const localStorageKey = (s3Url: string) => {
  let key = s3Url;

  // match hash and file name
  const matches = s3Url.match(/.+\/([a-f0-9]+\/.+?$)/);
  if (matches) {
    key = matches[1];
  }

  return key;
};

const tryFetchFromLocalStorage = (s3Url: string) => {
  try {
    const key = localStorageKey(s3Url);

    return localStorage.getItem(key);
  } catch {
    return undefined;
  }
};

const trySaveToLocalStorage = (s3Url: string, qdl: string) => {
  try {
    const key = localStorageKey(s3Url);
    localStorage.setItem(key, qdl);
  } catch {
    // do nothing
  }
};

async function withQdlsAsync(entities: { courseContents: { [string]: CourseContent } }) {
  const { courseContents } = entities;
  const qdls = await Promise.all(
    F.map(c => [c.uid, c.s3Url], courseContents).map<Promise<UidQdlTuple>>(([uid, s3Url]) => {
      // first, try to load from local storage
      let qdl = tryFetchFromLocalStorage(s3Url);
      if (qdl) return Promise.resolve([uid, qdl]);

      // when cache missed, load from s3
      return fetch(s3Url)
        .then(resp => resp.arrayBuffer())
        .then(unpackZipQdl)
        .then(qdl => {
          trySaveToLocalStorage(s3Url, qdl);
          return [uid, qdl];
        });
    })
  );
  return {
    ...entities,
    qdls: F.fromPairs(qdls),
  };
}

function addTimestamps(entities: any, timestamp: number) {
  return L.set(
    [
      L.entries, // key value pairs
      L.when(([k]) => k !== 'qdls'), // contests, coures, ...
      1, // we only need value part
      L.values, // pojo objects
      'fetchedAt',
    ],
    timestamp,
    entities
  );
}

function fetchContests() {
  return from(api.fetchContests()).pipe(
    getData(),
    mergeMap(data => {
      const { entities } = normalize(data.contests, [contestSchema]);
      return from(withQdlsAsync(entities)).pipe(
        map(entities => addTimestamps(entities, Date.now())),
        mergeMap(entities => from([fetchEntitiesSuccess({ targetType: 'contests', entities }), fetchProgressBegin()]))
      );
    })
  );
}

function fetchContest(uid: string) {
  return from(api.fetchContest(uid)).pipe(
    getData(),
    mergeMap(data => {
      const { entities } = normalize(data.contest, contestSchema);
      return from(withQdlsAsync(entities)).pipe(
        map(entities => addTimestamps(entities, Date.now())),
        map(entities => fetchEntitiesSuccess({ targetType: 'contests', entities }))
      );
    })
  );
}

function startFetching(payload: FetchEntitiesPayload): ObservableAction {
  switch (payload.targetType) {
    case 'contests':
      return fetchContests();
    case 'contest':
      return fetchContest(payload.uid);
    default:
      /*:: (payload.targetType: empty) */
      return throwError(new TypeError(`Unknown fetch targetType: ${payload.targetType}`));
  }
}

export function fetchEntitiesEpic(action$: ObservableAction): ObservableAction {
  return action$.pipe(
    ofType<ActionType<typeof fetchEntitiesBegin>>(fetchEntitiesBegin.type),
    mergeMap(({ payload }) => {
      return startFetching(payload).pipe(catchWith(fetchEntitiesFailure, payload));
    })
  );
}

export function fetchRankingsEpic(action$: ObservableAction): ObservableAction {
  return action$.pipe(
    ofType<ActionType<typeof fetchRankingsBegin>>(fetchRankingsBegin.type),
    switchMap(({ payload }) =>
      from(api.fetchRankings(payload.uid)).pipe(
        getData(),
        map(data => fetchRankingsSuccess({ uid: payload.uid, scoring: data.scoring })),
        catchWith(fetchRankingsFailure, payload)
      )
    )
  );
}

export default combineEpics(fetchEntitiesEpic, fetchRankingsEpic);
